import type { PedigreeType } from '@application/types';
import { AnimalSizeTypeOrdered } from '@application/types';
import {
  binarySelectOptions,
  enumSelectOptions,
  genderSelectOptions,
  pedigreeSelectOptions,
  useRouter,
} from '@application/utils';
import { CountryCode, DogCoat, DogColor, GenderEnum } from '@gql/graphql';
import type { SelectOption } from '@presentation/components';
import {
  AnimalSensitivity,
  InfoBox,
  InputDateField,
  InputField,
  InputFileField,
  InputLabel,
  InputRadioField,
  InputSelectField,
  InputWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { useMemo } from 'react';
import { FaFile } from 'react-icons/fa';
import type { MtAnimalBreederPageProps, MtAnimalsBreederPageFormValues } from '..';
import * as Styled from '../styled';

const weightOptions = [...Array(100).keys()].map((key) => ({
  label: `${key}kg`,
  value: key,
}));

export const MtAnimalBreederForm: FC<MtAnimalBreederPageProps> = (props) => {
  const { options, mode, disabledParentType = false, log } = props;
  const { values } = useFormikContext<MtAnimalsBreederPageFormValues>();
  const { translate } = useTranslation();
  const { countryCode } = useRouter();

  const sizeOptions = useMemo(() => {
    return enumSelectOptions(
      translate,
      'enum.animalSize',
      AnimalSizeTypeOrdered as any,
    ) as SelectOption<string>[];
  }, [translate]);

  const genderOptions = useMemo(() => {
    return genderSelectOptions(translate, {
      showIcons: true,
      showText: false,
      showOther: false,
    });
  }, [translate]);

  const colorOptions = useMemo(() => {
    const opts = enumSelectOptions(
      translate,
      'enum.dogColor',
      DogColor,
      true,
    ) as SelectOption<string>[];

    const other = opts.splice(
      opts.findIndex((option) => option.value === DogColor.OTHER),
      1,
    )?.[0];
    return [...opts, { ...other, ignoreSearch: true }];
  }, [translate]);

  const coatOptions = useMemo(() => {
    const opts = enumSelectOptions(
      translate,
      'enum.dogCoat',
      DogCoat,
      true,
    ) as SelectOption<string>[];
    const other = opts.splice(
      opts.findIndex((option) => option.value === DogCoat.OTHER),
      1,
    )?.[0];
    return [...opts, { ...other, ignoreSearch: true }];
  }, [translate]);

  const pedigreeOptions = useMemo(() => {
    return pedigreeSelectOptions(translate, {
      showText: true,
      showIcons: false,
      value: values.dogInfo?.pedigree,
    });
  }, [translate, values.dogInfo?.pedigree]);

  const microshipNumberIsRequired = useMemo(() => {
    return countryCode !== CountryCode.DE && values.gender === GenderEnum.F;
  }, [countryCode, values.gender]);

  const passportFileIsRequired = useMemo(() => {
    return countryCode === CountryCode.DE && values.gender === GenderEnum.F;
  }, [countryCode, values.gender]);

  return (
    <>
      <InputWrapper>
        <InputWrapper split>
          <InputLabel required>{translate('common.inputs.profilePicture.label')}</InputLabel>
          <Styled.ImageWrapperContainer>
            <Styled.ImageWrapper>
              <InputFileField
                name="images[0]"
                accept="image/*,.heic,.heif"
                disabled={mode === 'view'}
                log={log}
              />
            </Styled.ImageWrapper>
          </Styled.ImageWrapperContainer>
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split>
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.animalName.label')}</InputLabel>
          <InputField name="name" disabled={mode === 'view'} />
        </InputWrapper>

        <InputWrapper>
          <InputWrapper>
            <InputLabel required>
              {translate('common.inputs.breedWithoutCrossbreeds.label')}
            </InputLabel>
            <InputSelectField<number>
              disabled={mode === 'view'}
              name="dogInfo.breedId"
              options={options.breeds as SelectOption<number>[]}
            />
          </InputWrapper>

          <InputWrapper>
            <InputLabel required>{translate('common.inputs.gender.label')}</InputLabel>
            <InputRadioField<GenderEnum>
              name="gender"
              grow
              disabled={mode === 'view' || disabledParentType}
              options={genderOptions}
            />
          </InputWrapper>

          <InputWrapper>
            <InputWrapper>
              <InputLabel>{translate('common.inputs.size.label')}</InputLabel>
              <InputSelectField<string>
                name="size"
                disabled={mode === 'view'}
                options={sizeOptions}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{translate('common.inputs.weight.label')}</InputLabel>
              <InputSelectField<number>
                name="weight"
                disabled={mode === 'view'}
                options={weightOptions}
              />
            </InputWrapper>
          </InputWrapper>
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split last>
        <InputWrapper>
          <InputWrapper>
            <InputLabel>{translate('common.inputs.color.label')}</InputLabel>
            <InputSelectField<string>
              name="dogInfo.color"
              disabled={mode === 'view'}
              options={colorOptions}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{translate('common.inputs.coat.label')}</InputLabel>
            <InputSelectField<string>
              name="dogInfo.coat"
              disabled={mode === 'view'}
              options={coatOptions}
            />
          </InputWrapper>
        </InputWrapper>

        {/* Birthday inputs */}
        <InputWrapper>
          <InputLabel>{translate('common.inputs.knownBirthday.label')}</InputLabel>
          <InputRadioField
            grow
            name="birthdayType"
            disabled={mode === 'view'}
            options={[
              { value: 'DATE', label: translate('glossary.exactKnown') },
              { value: 'MONTH', label: translate('glossary.monthKnown') },
            ]}
          />
        </InputWrapper>
        <InputWrapper>
          <InputLabel required>
            {translate(
              values.birthdayType === 'DATE'
                ? 'common.inputs.animalBirthday.label'
                : 'common.inputs.animalBirthdayMonth.label',
            )}
          </InputLabel>
          <InputDateField
            name="birthday"
            disableFutureDates
            calendarProps={{
              maxDetail: values.birthdayType === 'MONTH' ? 'year' : undefined,
              defaultView: 'decade',
            }}
            disabled={mode === 'view'}
          />
        </InputWrapper>

        <InputWrapper>
          <InputLabel required>{translate('common.inputs.breederIsDogOwner.label')}</InputLabel>
          <InputRadioField<boolean>
            disabled={mode === 'view'}
            grow
            name="dogInfo.breederIsDogOwner"
            options={binarySelectOptions(translate)}
          />
        </InputWrapper>
      </InputWrapper>

      <InputWrapper>
        <InputLabel required={passportFileIsRequired}>
          {translate('common.inputs.passport.label')}
        </InputLabel>
        <InfoBox>{translate('common.inputs.passport.explanation')}</InfoBox>
        <InputFileField
          name="passportFile"
          disabled={mode === 'view'}
          icon={<FaFile />}
          placeholder={`${translate('common.inputs.passport.placeholder')}\n Max 8 MB`}
          accept=".pdf, .doc., image/*, .heic, .heif"
          isImage={false}
        />
      </InputWrapper>

      <InputWrapper split>
        <InputLabel>{translate('common.inputs.titles.label')}</InputLabel>
        <InputField name="dogInfo.competitionTitles" textarea disabled={mode === 'view'} />
      </InputWrapper>
      <InputWrapper split last>
        <InputLabel>{translate('common.inputs.testResults.label')}</InputLabel>
        <InputField name="dogInfo.healthTestsResults" textarea disabled={mode === 'view'} />
      </InputWrapper>
      <InputWrapper split>
        <InputLabel required={microshipNumberIsRequired}>
          {translate('common.inputs.microchipNumber.label')}
        </InputLabel>
        <InputField disabled={mode === 'view'} name="dogInfo.microshipNumber" />
      </InputWrapper>
      <InputWrapper split>
        <AnimalSensitivity mode={mode} />
      </InputWrapper>
      {/* Pedigree inputs */}
      <>
        <InputWrapper split>
          <InputLabel required>{translate('common.inputs.pedigree.label')}</InputLabel>
          <InputSelectField<PedigreeType>
            useSearch={false}
            disabled={mode === 'view'}
            name="dogInfo.pedigree"
            options={pedigreeOptions}
          />
        </InputWrapper>
        {values.dogInfo?.pedigree != null && (
          <>
            {values.dogInfo?.pedigree !== 'FCI' && (
              <InputWrapper split>
                <InputLabel>{translate('common.inputs.otherPedigree.label')}</InputLabel>
                <InputField
                  disabled={mode === 'view'}
                  name="dogInfo.pedigree"
                  placeholder={translate('common.inputs.pedigreeNumber.placeholder')}
                />
              </InputWrapper>
            )}
            <InputWrapper split last>
              <InputLabel>{translate('common.inputs.pedigreeNumber.label')}</InputLabel>
              <InputField disabled={mode === 'view'} name="dogInfo.pedigreeNumber" />
            </InputWrapper>
          </>
        )}
      </>
    </>
  );
};
